/**
 * NutrientIcon — mirror of mobile components/ui/NutrientIcon.tsx.
 *
 * Illustrated clay nutrient art (public/nutrients/*.png, copied from the app's
 * assets/nutrients). Accepts canonical keys (`addedSugar`) or field names
 * (`addedSugarsG`, `sodiumMg`, `fluidMl`, `water`). Nutrients without art
 * (trans fat) render a small dot in their NutrientColors hue.
 */

import type { CSSProperties } from 'react'
import { NutrientColors, NutrientCssVar, type NutrientColorKey } from '@/theme/tokens'

const ICONS: Record<string, string> = {
  addedSugar: '/nutrients/added-sugar.png',
  sugars: '/nutrients/sugars.png',
  saturatedFat: '/nutrients/saturated-fat.png',
  calories: '/nutrients/calories.png',
  fiber: '/nutrients/fiber.png',
  carbs: '/nutrients/carb.png',
  fat: '/nutrients/fat.png',
  cholesterol: '/nutrients/cholesterol.png',
  sodium: '/nutrients/sodium.png',
  protein: '/nutrients/protein.png',
  fluid: '/nutrients/water.png',
  coffee: '/nutrients/coffee.png',
}

// Map of common verbose/db keys → canonical icon / colour keys.
const CANONICAL: Record<string, string> = {
  sodiumMg: 'sodium',
  proteinG: 'protein',
  carbsG: 'carbs',
  fatG: 'fat',
  addedSugarsG: 'addedSugar',
  sugarsG: 'sugars',
  fiberG: 'fiber',
  saturatedFatG: 'saturatedFat',
  transFatG: 'transFat',
  cholesterolMg: 'cholesterol',
  fluidMl: 'fluid',
  water: 'fluid',
}

/** Canonical NutrientColors key for any nutrient field name (`addedSugarsG` → `addedSugar`). */
export function nutrientColorKey(nutrientKey: string): NutrientColorKey | undefined {
  const key = CANONICAL[nutrientKey] ?? nutrientKey
  return key in NutrientColors.light ? (key as NutrientColorKey) : undefined
}

interface NutrientIconProps {
  nutrientKey: string
  size?: number
  className?: string
  style?: CSSProperties
}

export function NutrientIcon({ nutrientKey, size = 24, className, style }: NutrientIconProps) {
  const key = CANONICAL[nutrientKey] ?? nutrientKey
  const source = ICONS[key]
  if (source) {
    return (
      <img
        src={source}
        alt=""
        aria-hidden
        width={size}
        height={size}
        className={className}
        style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0, ...style }}
      />
    )
  }
  const colorKey = nutrientColorKey(nutrientKey)
  const dot = Math.round(size * 0.42)
  return (
    <span
      aria-hidden
      className={className}
      style={{ width: size, height: size, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ...style }}
    >
      <span
        style={{
          width: dot,
          height: dot,
          borderRadius: dot / 2,
          // Theme-aware CSS variable (= NutrientColors[scheme][key]).
          backgroundColor: NutrientCssVar[colorKey ?? 'transFat'],
        }}
      />
    </span>
  )
}

export function hasNutrientIcon(nutrientKey: string): boolean {
  const key = CANONICAL[nutrientKey] ?? nutrientKey
  return ICONS[key] != null
}
